import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { api, errMsg, getUtm, track } from "../lib/api";
import { useSeo, useSite } from "../lib/site";
import { StepServices, StepDetails, validateDetails } from "../components/quote/StepsA";
import { StepPhotos } from "../components/quote/StepPhotos";
import { StepLocation, StepContact, StepReview, PricingStatus } from "../components/quote/StepsB";
import { CallButton } from "../components/site/Blocks";
import { AREA_LINE } from "../lib/cities";

const STEPS = ["Services", "Job details", "Photos", "Location & timing", "Contact", "Review"];
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const initial = () => ({
  services: [], details: { junk: {}, handyman: {}, cleanout: {}, yard: {}, senior: {}, other: {} }, city: "", zip: "", address: "", preferred_date: "", preferred_window: "", flexible: false,
  contact: { name: "", phone: "", email: "", preferred_contact: "phone" }, senior_requested: false, arranging_for_other: false, other_person_name: "", other_person_relationship: "",
  other_permission: false, lead_source: "", consent_response: false, marketing_consent: false, website: "",
});

function validate(step, form, photos) {
  const e = {};
  if (step === 0 && !form.services.length) e.services = "Please choose at least one service, or “Not sure”.";
  if (step === 1) Object.assign(e, validateDetails(form));
  if (step === 2 && photos.some((p) => p.status === "uploading")) e.photos = "Please wait for photos to finish uploading.";
  if (step === 3) {
    if (!form.city) e.city = "Please choose your city, or “Other / nearby area”.";
    if (!/^\d{5}$/.test(form.zip)) e.zip = "Please enter a 5-digit ZIP code.";
    if (form.address.trim().length < 5) e.address = "Please enter the service address so we can plan travel and access.";
  }
  if (step === 4) {
    const c = form.contact;
    if (!c.name.trim()) e.name = "Please enter your name.";
    if (c.phone.replace(/\D/g, "").length < 10) e.phone = "Please enter a 10-digit phone number.";
    if (c.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c.email)) e.email = "Please check your email address.";
    if (c.preferred_contact === "email" && !c.email) e.email = "Please add an email, or choose phone or text.";
    if (form.arranging_for_other && !form.other_permission) e.other_permission = "Please confirm they've given permission.";
    if (!form.consent_response) e.consent_response = "Please allow us to contact you about this request.";
  }
  return e;
}

function Progress({ step }) {
  return (
    <div className="mb-8" data-testid="quote-progress">
      <p className="text-base font-bold" aria-live="polite">Step {step + 1} of {STEPS.length}: {STEPS[step]}</p>
      <div className="mt-2 flex gap-1.5" aria-hidden="true">
        {STEPS.map((s, i) => <div key={s} className={`h-2.5 flex-1 rounded-full ${i <= step ? "bg-[#D9232E]" : "bg-neutral-300"}`} />)}
      </div>
    </div>
  );
}

function Success({ result }) {
  return (
    <div className="card fade-up border-2 border-green-700" data-testid="quote-success" role="status">
      <CheckCircle2 className="h-12 w-12 text-green-700" aria-hidden="true" />
      <h2 className="mt-3 text-3xl font-bold uppercase">Request received</h2>
      <p className="mt-2 text-lg">Your reference number is <strong className="font-display text-3xl" data-testid="quote-success-ref">{result.ref}</strong></p>
      <h3 className="mt-6 text-xl font-bold uppercase">What happens next</h3>
      <ol className="mt-2 list-decimal space-y-1 pl-6 text-base">
        <li>We review your request and photos.</li>
        <li>We contact you by your preferred method and send a written estimate.</li>
        <li>You approve the estimate, and then we confirm an appointment together.</li>
      </ol>
      <p className="mt-4 text-base text-neutral-700">Your preferred time is not yet a confirmed appointment.</p>
      <div className="mt-5"><PricingStatus assessment={{ pricing_status: result.pricing_status, estimate: result.estimate }} /></div>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row"><CallButton className="btn-outline" label="Questions? Call us" testid="success-call-btn" /><Link to="/" className="btn-dark" data-testid="success-home-btn">Back to home</Link></div>
    </div>
  );
}

export default function Quote() {
  useSeo("Request a Free Estimate", "Request a free written estimate in Albuquerque, Rio Rancho, Los Lunas, Corrales or Santa Fe for handyman help, junk removal, yard cleanup or a property cleanout in Albuquerque, NM.");
  const { site } = useSite();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [assessment, setAssessment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);
  const meta = useRef({ key: uid(), started: Date.now(), tracked: false });
  const topRef = useRef(null);

  useEffect(() => {
    if (!meta.current.tracked && (form.services.length || step > 0)) {
      meta.current.tracked = true;
      track("form_start");
    }
  }, [form.services.length, step]);

  const clearErr = (k) => setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  const set = (k, v) => { clearErr(k); setForm((f) => ({ ...f, [k]: v })); };
  const setContact = (k, v) => { clearErr(k); setForm((f) => ({ ...f, contact: { ...f.contact, [k]: v } })); };
  const setDetail = (svc, k, v) => { clearErr(`${svc}_${k}`); setForm((f) => ({ ...f, details: { ...f.details, [svc]: { ...f.details[svc], [k]: v } } })); };

  const payload = () => ({
    ...form, idempotency_key: meta.current.key, elapsed_ms: Date.now() - meta.current.started, utm: getUtm(),
    details: Object.fromEntries(Object.entries(form.details).filter(([k]) => form.services.includes(k) || (k === "other" && form.services.includes("not_sure")))),
    photos: photos.filter((p) => p.status === "done").map((p) => ({ id: p.id, token: p.token })),
  });

  const goTo = (i) => {
    setStep(i);
    setErrors({});
    setSubmitError("");
    setTimeout(() => topRef.current?.focus(), 50);
  };

  const next = async () => {
    const e = validate(step, form, photos);
    setErrors(e);
    if (Object.keys(e).length) {
      setTimeout(() => document.getElementById(Object.keys(e)[0])?.focus(), 50);
      return;
    }
    if (step === 4) {
      setAssessment(null);
      setSubmitError("");
      try {
        const r = await api.post("/quote-requests/preview", payload());
        setAssessment(r.data);
      } catch (err) {
        setSubmitError(errMsg(err));
        return;
      }
    }
    goTo(step + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const r = await api.post("/quote-requests", payload());
      track("form_complete");
      setResult(r.data);
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(errMsg(err, "We couldn't save your request. Please try again, or call or text us."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F5F5F5]" data-testid="quote-page">
      <div className="wrap max-w-3xl py-10 sm:py-14">
        <p className="eyebrow">Free estimate</p>
        <h1 className="mt-2 text-4xl font-extrabold uppercase leading-tight sm:text-5xl">Tell us what you need</h1>
        <p className="mt-2 text-base font-bold" data-testid="quote-area-line">{AREA_LINE}</p>
        <p className="mt-2 mb-8 text-lg text-neutral-700">About 3 minutes. Rather talk? <CallButton className="inline-flex items-center gap-1 font-bold text-[#B81B25] underline" label="Call us" testid="quote-intro-call" /></p>
        {result ? <Success result={result} /> : (
          <div className="rounded-lg border border-neutral-200 bg-white p-5 sm:p-8">
            <div ref={topRef} tabIndex={-1} className="outline-none"><Progress step={step} /></div>
            {step === 0 && <StepServices form={form} setForm={setForm} errors={errors} />}
            {step === 1 && <StepDetails form={form} setDetail={setDetail} errors={errors} site={site} />}
            {step === 2 && <StepPhotos photos={photos} setPhotos={setPhotos} />}
            {step === 3 && <StepLocation form={form} set={set} errors={errors} />}
            {step === 4 && <StepContact form={form} set={set} setContact={setContact} errors={errors} />}
            {step === 5 && (assessment ? <StepReview form={form} photos={photos} assessment={assessment} goTo={goTo} /> : <p className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />Preparing your summary…</p>)}
            {errors.photos && <p className="field-error" role="alert">{errors.photos}</p>}
            {Object.values(errors).some(Boolean) && step !== 0 && <p className="mt-4 rounded-md bg-red-50 p-3 font-bold text-[#B81B25]" role="alert" data-testid="quote-step-errors">Please fix the highlighted fields above.</p>}
            {submitError && <p className="mt-4 rounded-md border-2 border-[#B81B25] bg-red-50 p-4 font-bold text-[#B81B25]" role="alert" data-testid="quote-submit-error">{submitError}</p>}
            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              {step > 0 ? <button type="button" className="btn-outline" onClick={() => goTo(step - 1)} data-testid="quote-back-btn"><ArrowLeft className="h-5 w-5" />Back</button> : <span />}
              {step < 5 ? (
                <button type="button" className="btn-red" onClick={next} data-testid="quote-next-btn">{step === 2 && !photos.length ? "Skip photos" : "Continue"}<ArrowRight className="h-5 w-5" /></button>
              ) : (
                <button type="button" className="btn-red" onClick={submit} disabled={submitting || !assessment} data-testid="quote-submit-btn">{submitting ? <><Loader2 className="h-5 w-5 animate-spin" />Sending…</> : "Send my request"}</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
